import React from "react";
import { useTranslation } from "react-i18next";

import { Disclosure } from "./request-cards/cardShared";
import {
  MessageReferences,
  type MessageReferencesProps,
} from "./MessageReferences";
import { usePresignedDocumentUrls } from "../hooks/usePresignedDocumentUrls";
import { AllSimilarMachineNote, isMachineScopeMismatch } from "./MachineScopeMarker";
import { ReferenceType } from "../types/assistant";

/**
 * The full per-to-do grounding-source panel, shown inside a to-do card under a
 * quiet "Quellen" disclosure (default collapsed). This is the superior source
 * rendering (preview, type-grouped document cards, open links, dismiss checkbox)
 * that used to live only in the bottom Quellenrecherche list; it now sits with
 * the customer request it answers. Renders nothing when there are no references.
 *
 * Takes the same callbacks as {@link MessageReferences} so the host wires its
 * hover/open/toggle handlers once and reuses them for every to-do group.
 */
export function CustomerRequestSourceReferences({
  references,
  autoExpandReferenceIds,
  intro,
  ...rest
}: MessageReferencesProps & {
  /** The subset of highlights that should force this group open (deliberate
   *  navigation: an inline-citation click / active paragraph). Fact-hover
   *  highlights are intentionally excluded so hovering facts no longer pops
   *  collapsed groups open — instead the header is highlighted (see below).
   *  Falls back to `highlightedReferenceIds` when a caller does not pass it. */
  autoExpandReferenceIds?: Set<string> | null;
  /** The AI's explanation for why these sources were provided — rendered as
   *  plain introduction text above the source list, inside this same
   *  disclosure. A to-do with an explanation but no citations still renders
   *  this group (intro-only, no source list below it). */
  intro?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const count = references?.length ?? 0;

  // Pre-fetch presigned document URLs on mount, even though the panel below is
  // collapsed by default. MessageReferences owns this fetch, but it sits inside
  // the collapsed <Disclosure> and would stay unmounted (and unfetched) until
  // the agent expands "Quellen" — so a document click would fall back to the
  // slower click-time single fetch. Hoisting the fetch here (this wrapper is
  // always mounted) restores pre-generated-on-load links; the result is injected
  // into the inner MessageReferences, which then skips its own fetch.
  const documentIds = React.useMemo(
    () =>
      (references ?? [])
        .filter((r) => r.type === ReferenceType.Document && r.sourceDocumentId)
        .map((r) => r.sourceDocumentId as string),
    [references],
  );
  const { urls, isError } = usePresignedDocumentUrls(documentIds);

  // Nothing here matched the customer's machine. Per-card markers would then be on
  // EVERY card, which stops distinguishing anything — say it once, above the list,
  // and let the cards go quiet again.
  const allSimilar =
    count > 0 && (references ?? []).every(isMachineScopeMismatch);
  // The note names the machine type, so it only replaces the markers when that is
  // what every source got wrong. A group off on a cross-cutting fact only keeps its
  // per-card markers -- there is no type to name in a note.
  const expectedLabel = allSimilar ? references![0].scopeExpectedLabel : null;

  // The note REPLACES the markers rather than sitting on top of them, so the cards
  // below are handed references with the scope stamp cleared.
  const shownReferences = React.useMemo(
    () =>
      expectedLabel
        ? (references ?? []).map((r) => ({
            ...r,
            scopeExpectedLabel: null,
            // Cleared too, or a source that is off on BOTH would keep a bulb next to
            // the note that already says it.
            scopeFactLabel: null,
          }))
        : references,
    [expectedLabel, references],
  );

  const highlighted = rest.highlightedReferenceIds;

  // Auto-expand only for deliberate navigation (an inline-citation click /
  // active paragraph), so the target card is mounted and the jump lands. Fact
  // hover is deliberately NOT in `autoExpandReferenceIds`, so hovering a fact no
  // longer pops collapsed groups open (the annoying multi-customerRequest case) — it just
  // highlights this header instead (see `headerHighlighted`). Falls back to the
  // full highlight set for callers that don't pass the narrower one.
  // Only fall back to the full highlight set when a caller does NOT pass the
  // narrower set at all (undefined). A caller that passes `null` means "nothing
  // should force-expand right now" (e.g. fact hover) — honour that, don't fall
  // back, or fact hover would still pop the group open.
  const expandOn =
    autoExpandReferenceIds !== undefined ? autoExpandReferenceIds : highlighted;
  React.useEffect(() => {
    if (open || !expandOn) return;
    if (references?.some((r) => expandOn.has(r.id))) setOpen(true);
  }, [expandOn, references, open]);

  // Collapsed group whose "Quellen" the current highlight lives in: tint the
  // header so the agent knows expanding it reveals the source, without forcing
  // it open.
  const headerHighlighted =
    !open && !!highlighted && (references?.some((r) => highlighted.has(r.id)) ?? false);

  if (count === 0 && !intro) return null;

  return (
    <Disclosure
      open={open}
      onToggle={() => setOpen((v) => !v)}
      label={
        <span
          className={`inline-flex items-center gap-1.5 rounded px-1 -mx-1 transition-colors${headerHighlighted ? " bg-primary/10 font-medium text-primary" : ""}`}
        >
          {t("customerRequestCards.sources", "Quellen")}
        </span>
      }
    >
      {intro && <div className="mb-2">{intro}</div>}
      {expectedLabel && <AllSimilarMachineNote expectedLabel={expectedLabel} />}
      {count > 0 && (
        <MessageReferences
          references={shownReferences}
          {...rest}
          presignedUrls={urls}
          isErrorUrls={isError}
        />
      )}
    </Disclosure>
  );
}
